import type { PointerEvent as ReactPointerEvent } from "react";
import type { ReactNode } from "react";
import type { RenderBlock, RenderPlan } from "@pushback-and-chaos/documents";
import type { DocumentKind } from "@pushback-and-chaos/contracts";
import { tintFor } from "./tints.js";

/**
 * Frame 12 — a card dragged into the right-hand area, expanded into the full
 * document, layered over the narration. Every block role packages/documents
 * can emit is rendered here; a role added there without a case here would be
 * a compile error, not a silent blank.
 *
 * What is deliberately NOT rendered anywhere in this file:
 *   - `card.kind`      — a bare DocumentKind. Used only to pick a tint.
 *   - `card.stackHint` — it is literally `${authored_by} · ${fidelity}`, so
 *     rendering it would put a bare Fidelity enum on screen. Fidelity
 *     reaches the learner the way ADR 0003 intends: as authored banner text
 *     inside the document body, produced by the fidelity transform.
 */
export function DocumentReader({
  plan,
  kind,
  onClose,
  onHandlePointerDown,
}: {
  plan: RenderPlan;
  kind: DocumentKind;
  onClose: () => void;
  /** Drag handle (organization pass): the header repositions the open document. */
  onHandlePointerDown?: ((e: ReactPointerEvent<HTMLElement>) => void) | undefined;
}): ReactNode {
  const tint = tintFor(kind);
  return (
    <article
      // A min-h-0 flex item of the (column-flex, max-height-capped) drag
      // wrapper: as tall as its content until the cap, then the CONTENT region
      // below the header scrolls. A post-it stays post-it sized; a seventy-row
      // GL extract caps at the window and scrolls internally, with the title
      // and "put it down." always in reach.
      className="relative flex min-h-0 flex-col overflow-hidden rounded-open border"
      style={{ borderColor: tint.edge, background: tint.fill }}
      aria-label={plan.card.title}
    >
      <header
        onPointerDown={onHandlePointerDown}
        // No longer sticky: the header sits ABOVE the scroll region instead of
        // riding inside it, which keeps the drag handle and close control
        // fixed while the body scrolls.
        className={`z-10 flex shrink-0 items-start justify-between gap-8 px-12 pt-12 pb-4 ${onHandlePointerDown === undefined ? "" : "cursor-grab select-none active:cursor-grabbing"}`}
        style={{ background: tint.fill }}
      >
        <h2 className="m-0 font-display text-lede font-bold">{plan.card.title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Put this down"
          className="cursor-pointer border-0 bg-transparent p-0 font-sans text-nav text-ink hover:bg-marker"
        >
          put it down.
        </button>
      </header>
      <div className="pc-scroll min-h-0 flex-1 px-12 pb-16">
        {plan.blocks.map((block, index) => (
          <Block key={`${block.role}-${String(index)}`} block={block} />
        ))}
      </div>
    </article>
  );
}

/**
 * ONE BLOCK, and the only renderer of it in the product.
 *
 * The Storyteller's desk pages long documents by SLICING THE DATA — a table
 * with fewer rows, a list with fewer items — and handing the slice straight
 * back to this component, so a page two of a financial pack is the same
 * renderer, the same tabular numerals and the same column headings as page
 * one. It never re-implements a role, which is what keeps a footnote a
 * footnote and a fidelity banner a fidelity banner wherever it is read.
 *
 * `data-pcs-unit` / `data-pcs-head` are measuring marks: the off-screen
 * column reads them to find out where a row starts and how tall a repeated
 * heading is. They carry no styling and change nothing about this render.
 */
export function DocumentBlock({
  block,
  startIndex = 0,
  note,
  part,
  cells,
}: {
  readonly block: RenderBlock;
  /** A footnote list continued onto a later page keeps counting from here. */
  readonly startIndex?: number;
  /** A wide table cut into column groups says which columns these are. */
  readonly note?: string | undefined;
  /**
   * A sheet drawn in three pieces, so the desk can page its ROWS without
   * re-drawing its title and its provenance strip thirty times over. Omitted
   * — everywhere but the paged desk — the whole sheet is drawn at once,
   * exactly as it always was.
   */
  readonly part?: "head" | "rows" | "foot" | undefined;
  /**
   * Which cells of a two-source disagreement to draw — 0 the first source, 1
   * the second, 2 the line that refuses to adjudicate. Omitted, all three.
   * The desk needs this because a contested pair can be taller than a short
   * panel and must then be read across two pages rather than drawn twice.
   */
  readonly cells?: readonly number[] | undefined;
}): ReactNode {
  return <Block block={block} startIndex={startIndex} note={note} part={part} cells={cells} />;
}

function Block({
  block,
  startIndex = 0,
  note,
  part,
  cells,
}: {
  block: RenderBlock;
  startIndex?: number;
  note?: string | undefined;
  part?: "head" | "rows" | "foot" | undefined;
  cells?: readonly number[] | undefined;
}): ReactNode {
  switch (block.role) {
    case "header":
      return <h3 className="mt-8 mb-3 font-display text-body font-bold">{block.text}</h3>;

    case "kv-pair":
      return (
        <dl className="m-0 mt-4 grid gap-x-6 gap-y-1" style={{ gridTemplateColumns: "max-content 1fr" }}>
          {block.pairs.map((pair) => (
            <div key={pair.key} className="contents" data-pcs-unit="">
              <dt className="font-sans text-body text-ink-soft">{pair.key}:</dt>
              <dd className="m-0 font-sans text-body">{pair.value}</dd>
            </div>
          ))}
        </dl>
      );

    case "table":
      // The one place in the fiction where a horizontal scrollbar is allowed:
      // a wide financial pack must never squeeze its own columns into
      // unreadability, and must never push the page sideways either.
      return (
        <div className="mt-6 max-w-full overflow-x-auto">
          {note === undefined ? null : <div className="mb-1 font-sans text-micro text-ink-soft">{note}</div>}
          <table className="w-full border-collapse font-sans text-small">
            <thead data-pcs-head="">
              <tr>
                <th className="border-b border-b-ink-soft px-3 py-2 text-left font-display font-bold whitespace-nowrap" />
                {block.columns.map((column) => (
                  <th key={column} className="border-b border-b-ink-soft px-3 py-2 text-right font-display font-bold whitespace-nowrap">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={`${row.label}-${String(rowIndex)}`} data-pcs-unit="">
                  <th scope="row" className="px-3 py-1 text-left font-sans font-normal whitespace-nowrap">
                    {row.label}
                  </th>
                  {row.values.map((value, valueIndex) => (
                    <td key={`${String(valueIndex)}-${value}`} className="px-3 py-1 text-right tabular-nums whitespace-nowrap">
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "paragraph":
      return <p className="mt-4 mb-0 max-w-[65ch] font-sans text-body">{block.text}</p>;

    /**
     * A mail client's header, not a key-value grid: bold sender, recipients in
     * the quiet ink, the subject as the document's real headline, and a thread
     * marker when this is a reply — the affordance that tells a reader there
     * was an earlier message they have not seen.
     */
    case "email-header":
      return (
        <header className="border-b border-b-ink-soft pb-4">
          <div className="font-sans text-body">
            <span className="font-display font-bold">{block.from}</span>
            <span className="ml-3 text-micro text-ink-faint">{block.sent}</span>
          </div>
          <div className="mt-1 font-sans text-small text-ink-soft">to {block.to.join(", ")}</div>
          {block.cc.length > 0 ? <div className="font-sans text-small text-ink-soft">cc {block.cc.join(", ")}</div> : null}
          <div className="mt-3 font-sans text-lede">{block.subject}</div>
          {block.inReplyTo !== null ? (
            <div className="mt-1 font-sans text-micro text-ink-faint">↩ a reply — there is an earlier message in this thread</div>
          ) : null}
        </header>
      );

    case "signature":
      return <p className="mt-8 mb-0 max-w-[65ch] font-sans text-body whitespace-pre-line">{block.text}</p>;

    /**
     * THE SHEET. Column letters, a frozen header, right-set tabular numerals,
     * indented roll-up rows, and the provenance strip — the shape of a real
     * export, which is what lets the ugliness the case authors (mixed date
     * formats, a stale revision tag, footnoted overrides) read as evidence
     * rather than as rendering noise.
     */
    case "sheet-table":
      if (part === "head") {
        return (
          <section className="mt-6">
            <div className="flex flex-wrap items-baseline justify-between gap-4">
              <h3 className="m-0 font-display text-body font-bold">{block.title}</h3>
              <span className="font-mono text-micro text-ink-soft">{block.unitsLine}</span>
            </div>
            {note === undefined ? null : <div className="mt-1 font-sans text-micro text-ink-soft">{note}</div>}
          </section>
        );
      }
      if (part === "foot") {
        return (
          <div className="mt-1 flex flex-wrap gap-x-6 font-mono text-micro text-ink-soft">
            <span>source: {block.source}</span>
            <span>as of {block.asOfDate}</span>
            {block.revision !== null ? <span className="bg-marker px-1 text-ink">{block.revision}</span> : null}
          </div>
        );
      }
      return (
        <section className={part === "rows" ? "" : "mt-6"}>
          {part === "rows" ? null : (
            <div className="flex flex-wrap items-baseline justify-between gap-4">
              <h3 className="m-0 font-display text-body font-bold">{block.title}</h3>
              <span className="font-mono text-micro text-ink-soft">{block.unitsLine}</span>
            </div>
          )}
          {note === undefined || part === "rows" ? null : <div className="mt-1 font-sans text-micro text-ink-soft">{note}</div>}
          <div className={`pc-scroll max-w-full overflow-auto border border-ink-soft ${part === "rows" ? "" : "mt-2 max-h-[24rem]"}`}>
            <table className="w-full border-collapse font-mono text-small">
              <thead className="sticky top-0 z-10" data-pcs-head="">
                <tr className="bg-paper">
                  <th className="border-b border-r border-ink-soft px-2 py-1 text-center font-normal text-ink-faint">A</th>
                  {block.columns.map((column) => (
                    <th key={column.letter} className="border-b border-r border-ink-soft px-3 py-1 text-center font-normal text-ink-faint">
                      {column.letter}
                    </th>
                  ))}
                </tr>
                <tr className="bg-paper">
                  <th className="border-b border-r border-b-ink border-r-ink-soft px-2 py-1 text-left font-sans font-bold whitespace-nowrap" />
                  {block.columns.map((column) => (
                    <th key={`${column.letter}-label`} className="border-b border-r border-b-ink border-r-ink-soft px-3 py-1 text-right font-sans font-bold whitespace-nowrap">
                      {column.label}
                      <span className="block font-sans text-micro font-normal text-ink-soft">{column.dimension}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr key={`${row.label}-${String(rowIndex)}`} data-pcs-unit="" className={(rowIndex + startIndex) % 2 === 1 ? "bg-grid-bg" : ""}>
                    <th scope="row" className={`border-r border-r-ink-soft px-2 py-1 text-left font-sans font-normal whitespace-nowrap ${row.indent ? "pl-6 text-ink-soft" : ""} ${row.emphasis ? "font-bold" : ""}`}>
                      {row.label}
                    </th>
                    {row.values.map((value, valueIndex) => (
                      <td key={`${String(valueIndex)}-${value}`} className={`border-r border-r-grid-line px-3 py-1 text-right tabular-nums whitespace-nowrap ${row.emphasis ? "font-bold" : ""}`}>
                        {value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {part === "rows" ? null : (
            <div className="mt-1 flex flex-wrap gap-x-6 font-mono text-micro text-ink-soft">
              <span>source: {block.source}</span>
              <span>as of {block.asOfDate}</span>
              {block.revision !== null ? <span className="bg-marker px-1 text-ink">{block.revision}</span> : null}
            </div>
          )}
        </section>
      );

    case "footnote-list":
      return (
        <ol className="mt-3 mb-0 max-w-[70ch] list-none p-0 font-sans text-micro text-ink-soft">
          {block.items.map((item, index) => (
            <li key={`${String(index)}-${item.slice(0, 16)}`} data-pcs-unit="" className="mt-1 flex gap-2">
              <span className="shrink-0 font-mono">({String(index + 1 + startIndex)})</span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      );

    /** A memo's letterhead: the TO/FROM/DATE/RE block above a heavy rule. */
    case "letterhead":
      return (
        <header className="border-b-2 border-b-ink pb-4">
          <dl className="m-0 grid gap-x-8 gap-y-1" style={{ gridTemplateColumns: "max-content 1fr" }}>
            {block.pairs.map((pair) => (
              <div key={pair.key} className="contents" data-pcs-unit="">
                <dt className="font-display text-small font-bold tracking-wide uppercase">{pair.key}:</dt>
                <dd className="m-0 font-sans text-body">{pair.value}</dd>
              </div>
            ))}
          </dl>
        </header>
      );

    /**
     * One transcript turn: the speaker column a reader's eye scans down, the
     * words beside it, and the noted non-verbal in the quiet ink — authored
     * texture that previously never rendered at all.
     */
    case "transcript-turn":
      return (
        <div className="mt-4 grid gap-x-4" style={{ gridTemplateColumns: "8rem 1fr" }}>
          <div className="pt-[2px] text-right font-display text-micro font-bold tracking-wide break-words uppercase">{block.speaker}</div>
          <div className="max-w-[60ch]">
            <p className="m-0 font-sans text-body">{block.text}</p>
            {block.nonVerbal !== null ? <p className="mt-1 mb-0 font-sans text-micro text-ink-faint italic">[{block.nonVerbal}]</p> : null}
          </div>
        </div>
      );

    /**
     * Frame 11 calls these "notes in margin" and "post-it note in your
     * handwriting". They are annotations ON the document, not part of its
     * body, so they are set in a hand face and rotated slightly — a reader
     * must never mistake somebody's biro for the printed record.
     *
     * They used to be pulled 3rem into the margin as well, which is exactly
     * the reader's own padding: the note sat flush on the card's edge while
     * every printed block kept its inset, and read as a layout fault rather
     * than a margin note (2026-09-08). The face and the tilt carry the
     * distinction on their own; the note now shares the body's left line.
     */
    case "annotation":
      return (
        <aside className="relative mt-6 w-fit max-w-[55ch] -rotate-[0.7deg] font-hand text-body" style={{ color: "#1b3a8f" }}>
          <span className="block">{block.text}</span>
          <span className="mt-1 block text-micro opacity-70">— {block.author}</span>
        </aside>
      );

    /**
     * The fidelity banner. This is how STALE / AGGREGATED / PARTIAL /
     * CONTESTED actually reach the learner: as a sentence the document
     * itself carries. Ruled off rather than boxed and coloured, because a
     * warning-coloured box would turn it back into a status label.
     */
    case "banner":
      return (
        <p className="mt-6 mb-0 max-w-[65ch] border-l-2 border-l-ink-soft pl-4 font-sans text-small text-ink-soft italic">{block.text}</p>
      );

    case "attachment-list":
      return (
        <ul className="mt-4 mb-0 list-none p-0 font-sans text-body">
          {block.items.map((item) => (
            <li key={item.name} data-pcs-unit="" className={item.available ? "" : "text-ink-faint"}>
              <span className="mr-2 opacity-60">↳</span>
              {item.name}
              {item.available ? null : <span className="ml-2 text-micro">(not attached)</span>}
            </li>
          ))}
        </ul>
      );

    case "redaction":
      return (
        <p className="mt-4 mb-0 font-mono text-small text-ink-soft">
          <span className="bg-ink px-1 text-ink">████</span> {block.text}
        </p>
      );

    /**
     * CONTESTED, and the highest-value rendering in the whole reader. It must
     * read as two sources disagreeing — two attributed voices side by side,
     * with nothing between them adjudicating — never as a document wearing a
     * "CONTESTED" label. There is no badge, no colour coding, and neither
     * side is styled as the correct one.
     */
    case "conflict-pair": {
      const shows = (cell: number): boolean => cells === undefined || cells.includes(cell);
      /*
       * SIDE BY SIDE when the whole document is drawn at once; STACKED on the
       * paged desk. Two cells in one row have the same top, so there is no
       * height between them to cut at — and a pair that does not fit would
       * either be drawn twice or re-laid-out one column wide and overflow the
       * page it was measured for. Stacked, each source has its own top, the
       * cut is real, and the two are still adjacent and still attributed with
       * nothing between them deciding.
       */
      const paged = cells !== undefined;
      return (
        <div className={`mt-6 grid gap-0 border-t border-t-ink-soft ${paged ? "" : "sm:grid-cols-2"}`} role="group" aria-label="Two sources disagree">
          {shows(0) ? (
            <div data-pcs-unit="" className={paged ? "px-4 py-3" : "border-r border-r-ink-soft px-4 py-3"}>
              <div className="font-display text-micro font-bold tracking-wide">{block.a.source}</div>
              <p className="mt-2 mb-0 font-sans text-body">{block.a.text}</p>
            </div>
          ) : null}
          {shows(1) ? (
            <div data-pcs-unit="" className="px-4 py-3">
              <div className="font-display text-micro font-bold tracking-wide">{block.b.source}</div>
              <p className="mt-2 mb-0 font-sans text-body">{block.b.text}</p>
            </div>
          ) : null}
          {shows(2) ? (
            <div data-pcs-unit="" className="col-span-full border-t border-t-ink-soft px-4 py-2 font-sans text-micro text-ink-soft">
              These two do not agree. Nothing here decides which is right.
            </div>
          ) : null}
        </div>
      );
    }

    default:
      return assertNever(block);
  }
}

function assertNever(block: never): never {
  throw new Error(`DocumentReader: unhandled block role ${JSON.stringify(block)}`);
}
